import { skipToken, useQuery } from '@tanstack/react-query';

import { Announcements } from '@air/api';
import { FIVE_MINUTES } from '@air/utilities';
import { type ApiResponse, callAirApi } from '@air/utils-api';

import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { canViewAnnouncements } from '~/utils/permissions/workspacePermissions';

export const PRIVATE_ANNOUNCEMENTS_LIST_KEY = 'PRIVATE_ANNOUNCEMENTS_LIST';

export type PrivateAnnouncementsListKey = [typeof PRIVATE_ANNOUNCEMENTS_LIST_KEY, { workspaceId: string }];

export const getPrivateAnnouncementsListKey = (workspaceId: string): PrivateAnnouncementsListKey => {
  return [PRIVATE_ANNOUNCEMENTS_LIST_KEY, { workspaceId }];
};

export type AnnouncementsListSuccess = ApiResponse<typeof Announcements.list>;

export type PrivateAnnouncementListItem = AnnouncementsListSuccess['data'][number];

export type PrivateAnnouncementsListData = PrivateAnnouncementListItem[];

const ANNOUNCEMENTS_PAGE_SIZE = 100;

export const fetchPrivateAnnouncementsAllPages = async (workspaceId: string): Promise<PrivateAnnouncementsListData> => {
  const announcements: PrivateAnnouncementListItem[] = [];
  let cursor: string | undefined;

  for (;;) {
    const response = await callAirApi<AnnouncementsListSuccess>(() =>
      Announcements.list({
        cursor,
        limit: ANNOUNCEMENTS_PAGE_SIZE,
        workspaceId,
      }),
    );

    announcements.push(...response.data);

    if (!response.pagination.hasMore || !response.pagination.cursor) {
      break;
    }

    cursor = response.pagination.cursor;
  }

  return announcements;
};

export const usePrivateAnnouncementsList = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const { announcementsEnabled } = useTemporaryFeatureFlags();
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const canReadAnnouncements = canViewAnnouncements(permissions);

  return useQuery({
    // A flag missing from LaunchDarkly reads as undefined, and react-query runs the query when enabled is undefined.
    enabled: Boolean(workspaceId && announcementsEnabled && canReadAnnouncements),
    queryFn: !workspaceId ? skipToken : () => fetchPrivateAnnouncementsAllPages(workspaceId),
    queryKey: workspaceId ? getPrivateAnnouncementsListKey(workspaceId) : [PRIVATE_ANNOUNCEMENTS_LIST_KEY],
    // staleTime alone never schedules a request, so without the interval an unpublished announcement
    // with no expiry stays on screen for as long as the reader keeps the page open.
    refetchInterval: FIVE_MINUTES,
    refetchOnWindowFocus: true,
    staleTime: FIVE_MINUTES,
  });
};
